// src/api/adminWaHealthApi.ts
// Adaptador del TABLERO GLOBAL DE SALUD WHATSAPP (super_admin, F5c) contra
// lealify-base routes/admin.js: una fila por sesión WAHA conocida (estado vivo
// del webhook F4 + última foto del cron 07:15 + gate anti-ban F3) y la línea
// de tiempo de wa_session_events.
import { baseApi } from './baseClient';

export type WaSemaforo = 'verde' | 'ambar' | 'rojo';

export interface WaGateRechazo {
  motivo: string;
  label: string;
  n: number;
  numeros: number;
  ultima: string;
}

export interface WaGateInfo {
  activo: boolean;
  restriccion?: 'ninguna' | 'frio' | 'todo';
  motivo?: string | null;
  label?: string | null;
  hasta?: string | null;          // YYYY-MM-DD en que se libera (si aplica)
  rechazos?: WaGateRechazo[];     // 7 días, agrupados por motivo
}

// `parts` viene tal cual del motor v2 (lib/waHealth.js); se tipa laxo porque
// las fotos viejas (v1) traen otras llaves y la tarjeta degrada sola.
export interface WaHealthSnapshot {
  available: boolean;
  session_status?: string;
  score?: number;
  semaforo?: WaSemaforo;
  veredicto?: string;
  parts?: Record<string, any> | null;
  measured_at?: string;
  stale?: boolean;
}

export interface HistoriaSalud {
  puntos: { dia: string; score: number; semaforo?: WaSemaforo; session_status?: string }[];
  dias_con_datos: number;
  delta7: number | null;          // variación vs la foto de hace ~7 días
  desde: string | null;
}

export interface WaSessionRow {
  session: string;
  tenant_id: number | null;
  tenant_name: string | null;
  tenant_status: string | null;
  live_status: string | null;     // tiempo real (webhook session.status)
  status_since: string | null;
  working_since: string | null;   // YYYY-MM-DD de la última vinculación
  snapshot: WaHealthSnapshot;
  gate: WaGateInfo | null;
  // Serie diaria del score (motor v2 en adelante) para la gráfica de tendencia.
  // Opcional: un backend anterior a 29-jul-2026 no la manda y la tarjeta degrada sola.
  historia?: HistoriaSalud | null;
}

export interface WaSessionEvent {
  session_name: string;
  tenant_id: number | null;
  status: string;
  prev_status: string | null;
  reason: string | null;
  detail: string | null;
  source: string;
  ts: string;
}

// Explicador del gate (30-jul-2026): el recorrido completo de canSendCold para un
// contacto — qué dato tiene cada regla, cuál decidió y cuáles quedaron anuladas por
// la conversación previa. Solo plataforma (expone internos como `repaired`).
export interface GateExplainPaso {
  n: number;
  regla: string;
  estado: 'ok' | 'bloquea' | 'PASA' | 'no_aplica' | 'sin_dato';
  dato: string;
  texto: string;
}

export interface GateExplainResult {
  tenant_id: number;
  phone: string;
  phone10: string | null;
  source: string;
  transaccional: boolean;
  veredicto: { ok: boolean; motivo: string | null; detalle: string | null; permanente: boolean; overridable: boolean };
  pasos: GateExplainPaso[];
  decidio: string | null;
  saltadas: string[];
  avisos: string[];
}

export const adminWaHealthApi = {
  overview: () =>
    baseApi.get<{ sessions: WaSessionRow[] }>('/admin/wa-health/overview'),
  events: (session?: string, limit = 50) =>
    baseApi.get<{ events: WaSessionEvent[] }>(
      `/admin/wa-health/events?${session ? `session=${encodeURIComponent(session)}&` : ''}limit=${limit}`),
  explain: (tenant: number, phone: string, source = 'campaign') =>
    baseApi.get<GateExplainResult>(
      `/admin/wa-health/explain?tenant=${tenant}&phone=${encodeURIComponent(phone)}&source=${source}`),
};
